import { apiRequest } from '@/shared/lib/api-client';

export type ChannelType = 'meta_official' | 'qr_code' | 'instagram';

/** Distribuição automática de tickets do canal. */
export type AutoAssignMode = 'off' | 'balance' | 'random' | 'sequential';

export type NumberStatus =
  | 'active'
  | 'inactive'
  | 'pending'
  | 'unregistered'
  | 'pending_qr'
  | 'revoked';

export interface WhatsappNumberRaw {
  id: number;
  phone_number_id: string | null; // null em qr_code antes do pareamento
  display_number: string | null;
  display_name: string | null;
  channel_type: ChannelType;
  provider: 'whatsmeow' | 'zapo';
  bsp: string | null;
  account_handle?: string | null;
  quality_rating: 'GREEN' | 'YELLOW' | 'RED' | null;
  messaging_tier: string | null;
  status: NumberStatus;
  registered_at: string | null;
  last_connected_at: string | null;
  disconnected_at: string | null;
  updated_at: string | null;
  account_id: number;
  account_status: 'active' | 'revoked' | 'expired' | 'pending';
  token_expires_at: string | null;
  created_via: 'manual' | 'embedded_signup';
  waba_id: string | null; // null em qr_code account
  default_chatbot_id: number | null;
  default_department_id: number | null;
  default_access_group_id: number | null;
  coexistence_mode: boolean;
  app_text_outbound: boolean;
  auto_close_inactive_minutes: number | null;
  offline_to_pending_minutes: number | null;
  auto_assign_mode: AutoAssignMode;
  hide_pending_content: boolean;
  send_closing_message_on_bulk: boolean;
  show_billing_badge: boolean;
  require_closing_reason: boolean;
  allow_message_edit?: boolean;
  text_batch_seconds: number | null;
  text_batch_mode?: 'fixed' | 'adaptive' | 'smart';
  text_batch_separator?: 'line' | 'blank_line' | 'space';
  text_batch_max_seconds?: number;
  ai_rewrite_enabled: boolean;
}

export interface WhatsappNumber {
  id: number;
  phoneNumberId: string | null;
  displayNumber: string | null;
  displayName: string | null;
  channelType: ChannelType;
  /** Motor do canal qr_code: 'whatsmeow' (Qr Code API) ou 'zapo' (V2). */
  provider: 'whatsmeow' | 'zapo';
  /** BSP de transporte: 'dialog360' pra canal via 360dialog; 'wame' pro
   *  Instagram (parceiro WAME); null = Meta direto. */
  bsp: string | null;
  /** @ da conta conectada (Instagram). */
  accountHandle: string | null;
  qualityRating: 'GREEN' | 'YELLOW' | 'RED' | null;
  messagingTier: string | null;
  status: NumberStatus;
  registeredAt: string | null;
  /** Última abertura do socket (inclui reconexões automáticas). */
  lastConnectedAt: string | null;
  /** Última desconexão REAL (botão Desconectar / logout pelo celular). */
  disconnectedAt: string | null;
  /** Última mudança no canal (qualquer ação — trigger set_updated_at). */
  updatedAt: string | null;
  accountId: number;
  accountStatus: 'active' | 'revoked' | 'expired' | 'pending';
  tokenExpiresAt: string | null;
  createdVia: 'manual' | 'embedded_signup';
  wabaId: string | null;
  defaultChatbotId: number | null;
  defaultDepartmentId: number | null;
  defaultAccessGroupId: number | null;
  coexistenceMode: boolean;
  appTextOutbound: boolean;
  showBillingBadge: boolean;
  requireClosingReason: boolean;
  /** Atendente pode editar msg enviada (≤15min, só QR). Nasce desligado. */
  allowMessageEdit: boolean;
  /** Agrupador de texto: null/0 = desligado; valor = segundos de espera. */
  textBatchSeconds: number | null;
  /** Modo do agrupador: fixo / adaptativo (default) / inteligente. */
  textBatchMode: 'fixed' | 'adaptive' | 'smart';
  /** Separação entre os textos do pacote: quebra de linha (default) / linha em branco / mesma linha. */
  textBatchSeparator: 'line' | 'blank_line' | 'space';
  /** Teto do agrupador (s) — máximo de espera desde o 1º texto. */
  textBatchMaxSeconds: number;
  aiRewriteEnabled: boolean;
  autoCloseInactiveMinutes: number | null;
  /** Minutos desconectado até os ativos do atendente voltarem pra Pendentes; null = desligado. */
  offlineToPendingMinutes: number | null;
  autoAssignMode: AutoAssignMode;
  hidePendingContent: boolean;
  sendClosingMessageOnBulk: boolean;
}

export interface ConnectManualInput {
  wabaId: string;
  phoneNumberId: string;
  accessToken: string;
  businessId?: string;
}

export interface ConnectEmbeddedInput {
  code: string;
  wabaId: string;
  phoneNumberId: string;
  businessId?: string;
  finishEvent?: string;
}

export interface ConnectEmbeddedResult {
  whatsapp_number_id: number;
  display_number: string;
  quality_rating: 'GREEN' | 'YELLOW' | 'RED' | null;
  messaging_tier: string | null;
  status: string;
}

export interface ConnectManualResult {
  whatsapp_account_id: number;
  whatsapp_number_id: number;
  display_number: string;
  quality_rating: 'GREEN' | 'YELLOW' | 'RED' | null;
  messaging_tier: string | null;
  status: string;
}

function map(n: WhatsappNumberRaw): WhatsappNumber {
  return {
    id: n.id,
    phoneNumberId: n.phone_number_id,
    displayNumber: n.display_number,
    displayName: n.display_name,
    channelType: n.channel_type,
    provider: n.provider,
    bsp: n.bsp ?? null,
    accountHandle: n.account_handle ?? null,
    qualityRating: n.quality_rating,
    messagingTier: n.messaging_tier,
    status: n.status,
    registeredAt: n.registered_at,
    lastConnectedAt: n.last_connected_at,
    disconnectedAt: n.disconnected_at,
    updatedAt: n.updated_at,
    accountId: n.account_id,
    accountStatus: n.account_status,
    tokenExpiresAt: n.token_expires_at,
    createdVia: n.created_via,
    wabaId: n.waba_id,
    defaultChatbotId: n.default_chatbot_id,
    defaultDepartmentId: n.default_department_id,
    defaultAccessGroupId: n.default_access_group_id,
    coexistenceMode: n.coexistence_mode ?? false,
    appTextOutbound: n.app_text_outbound ?? false,
    showBillingBadge: n.show_billing_badge ?? false,
    requireClosingReason: n.require_closing_reason ?? true,
    allowMessageEdit: n.allow_message_edit ?? false,
    textBatchSeconds: n.text_batch_seconds ?? null,
    textBatchMode: n.text_batch_mode ?? 'adaptive',
    textBatchSeparator: n.text_batch_separator ?? 'line',
    textBatchMaxSeconds: n.text_batch_max_seconds ?? 40,
    aiRewriteEnabled: n.ai_rewrite_enabled ?? false,
    autoCloseInactiveMinutes: n.auto_close_inactive_minutes ?? null,
    offlineToPendingMinutes: n.offline_to_pending_minutes ?? null,
    autoAssignMode: n.auto_assign_mode ?? 'off',
    hidePendingContent: n.hide_pending_content ?? false,
    sendClosingMessageOnBulk: n.send_closing_message_on_bulk ?? false,
  };
}

export async function listChannels(): Promise<WhatsappNumber[]> {
  const data = await apiRequest<WhatsappNumberRaw[]>('/whatsapp-numbers');
  return data.map(map);
}

/** Uso vs limites do plano (Super → Editar tenant). Usado pra barrar o
 *  "Adicionar" (canal/usuário) com toast antes de abrir o fluxo. */
export interface PlanUsage {
  max_channels: number | null;
  channels_active: number;
  max_users: number | null;
  users_active: number;
  /** Empresa "somente canais oficiais": o "Adicionar Canal" esconde QR, Dev
   *  App e WAME (a API também recusa). Ausente em API antiga = false. */
  official_only?: boolean;
}

export async function getPlanUsage(): Promise<PlanUsage> {
  return apiRequest<PlanUsage>('/whatsapp-numbers/plan-usage');
}

/** Canal que o user pode usar pra INICIAR atendimento (subset de /me/startable-channels). */
export interface StartableChannel {
  id: number;
  displayNumber: string | null;
  displayName: string | null;
  channelType: ChannelType;
  status: NumberStatus;
}

interface StartableChannelRaw {
  id: number;
  display_number: string | null;
  display_name: string | null;
  channel_type: ChannelType;
  status: NumberStatus;
}

/**
 * Canais que o USER LOGADO pode usar pra iniciar atendimento (vínculo
 * `user_whatsapp_numbers`). Regra: sem vínculo = lista vazia; admin também
 * restrito; sessão de support vê todos. Alimenta o modal "Iniciar atendimento".
 */
export async function listStartableChannels(): Promise<StartableChannel[]> {
  const data = await apiRequest<StartableChannelRaw[]>('/me/startable-channels');
  return data.map((n) => ({
    id: n.id,
    displayNumber: n.display_number,
    displayName: n.display_name,
    channelType: n.channel_type,
    status: n.status,
  }));
}

export async function connectManual(input: ConnectManualInput): Promise<ConnectManualResult> {
  return apiRequest<ConnectManualResult>('/whatsapp-accounts/connect-manual', {
    method: 'POST',
    body: {
      waba_id: input.wabaId,
      phone_number_id: input.phoneNumberId,
      access_token: input.accessToken,
      ...(input.businessId && { business_id: input.businessId }),
    },
  });
}

export async function connectDialog360(input: {
  label: string;
  apiKey: string;
}): Promise<{ whatsapp_number_id: number; webhook_configured: boolean; webhook_url: string }> {
  return apiRequest<{ whatsapp_number_id: number; webhook_configured: boolean; webhook_url: string }>(
    '/whatsapp-accounts/connect-360dialog',
    {
      method: 'POST',
      body: { label: input.label, api_key: input.apiKey },
    },
  );
}

/**
 * Instagram Direct via parceiro WAME: Server + Key da instância. 409
 * `WAME_WEBHOOK_IN_USE` (com `current_webhook` no contexto) = a instância já
 * manda mensagens pra outro sistema → confirmar e reenviar com
 * `confirmOverwrite`.
 */
export async function connectWame(input: {
  label: string;
  server: string;
  key: string;
  confirmOverwrite?: boolean;
}): Promise<{ whatsapp_number_id: number; webhook_configured: boolean; webhook_url: string }> {
  return apiRequest<{
    whatsapp_number_id: number;
    webhook_configured: boolean;
    webhook_url: string;
  }>('/whatsapp-accounts/connect-wame', {
    method: 'POST',
    body: {
      label: input.label,
      server: input.server,
      key: input.key,
      ...(input.confirmOverwrite && { confirm_overwrite: true }),
    },
  });
}

export async function connectEmbedded(input: ConnectEmbeddedInput): Promise<ConnectEmbeddedResult> {
  return apiRequest<ConnectEmbeddedResult>('/whatsapp-accounts/connect-embedded', {
    method: 'POST',
    body: {
      code: input.code,
      waba_id: input.wabaId,
      phone_number_id: input.phoneNumberId,
      ...(input.businessId && { business_id: input.businessId }),
      ...(input.finishEvent && { finish_event: input.finishEvent }),
    },
  });
}

export function setNumberStatus(id: number, status: 'active' | 'inactive'): Promise<void> {
  return apiRequest<void>(`/whatsapp-numbers/${id}`, { method: 'PATCH', body: { status } });
}

/**
 * Renomeia apelido interno do canal — não envia pra Meta. Funciona em
 * meta_official e qr_code.
 */
export function renameChannel(id: number, displayName: string): Promise<void> {
  return apiRequest<void>(`/whatsapp-numbers/${id}/rename`, {
    method: 'PATCH',
    body: { display_name: displayName },
  });
}

export interface UpdateChannelSettingsInput {
  display_name?: string;
  default_chatbot_id?: number | null;
  default_department_id?: number | null;
  default_access_group_id?: number | null;
  /** Minutos pra encerrar por inatividade; null = Não fechar. */
  auto_close_inactive_minutes?: number | null;
  /** Minutos desconectado até devolver os ativos do atendente pra Pendentes; null = desligado. */
  offline_to_pending_minutes?: number | null;
  /** Distribuição automática de tickets do canal. */
  auto_assign_mode?: AutoAssignMode;
  /** Fila cega: não-admin não vê prévia/conversa de pendentes até aceitar. */
  hide_pending_content?: boolean;
  /** Enviar msg de fechamento também no fechamento em massa (default off). */
  send_closing_message_on_bulk?: boolean;
  /** Selo de "mensagem paga" no balão (só meta_official). */
  show_billing_badge?: boolean;
  require_closing_reason?: boolean;
  allow_message_edit?: boolean;
  text_batch_seconds?: number | null;
  text_batch_mode?: 'fixed' | 'adaptive' | 'smart';
  text_batch_separator?: 'line' | 'blank_line' | 'space';
  text_batch_max_seconds?: number;
  ai_rewrite_enabled?: boolean;
}

/**
 * Atualiza configurações administrativas: apelido + chatbot default + dept
 * default. null em chatbot/dept = remover. Mexe num único endpoint admin-only.
 */
/**
 * O que aconteceu com o webhook do canal 360dialog ao salvar. Só vem em canal
 * 360dialog — nos outros o campo não existe. Vide `reapontarWebhookDialog360`
 * no servidor.
 */
export type WebhookReapontado =
  | { estado: 'ja_era_nosso' }
  | { estado: 'apontado' }
  | { estado: 'trazido_de_volta'; de: string }
  | { estado: 'falhou' };

export function updateChannelSettings(
  id: number,
  input: UpdateChannelSettingsInput,
): Promise<{ webhook?: WebhookReapontado }> {
  return apiRequest<{ webhook?: WebhookReapontado }>(`/whatsapp-numbers/${id}/settings`, {
    method: 'PATCH',
    body: input,
  });
}

export function disconnectNumber(id: number): Promise<void> {
  return apiRequest<void>(`/whatsapp-numbers/${id}`, { method: 'DELETE' });
}

// ───────── QR Code (whatsmeow) — (removido — vide whatsmeow worker) (G3) ─────────

export interface CreateQrChannelInput {
  label: string;
  /** Motor do canal: 'whatsmeow' (Qr Code API) ou 'zapo' (QR CODE V2). */
  provider?: 'whatsmeow' | 'zapo';
}

export interface CreateQrChannelResult {
  whatsapp_number_id: number;
  status: 'pending_qr';
}

export async function createQrChannel(input: CreateQrChannelInput): Promise<CreateQrChannelResult> {
  return apiRequest<CreateQrChannelResult>('/whatsapp-numbers/qr-code', {
    method: 'POST',
    body: input,
  });
}

export async function deleteQrChannel(numberId: number): Promise<void> {
  await apiRequest<void>(`/whatsapp-numbers/${numberId}/qr-code`, { method: 'DELETE' });
}

/**
 * Converte o canal QR entre motores (whatsmeow ↔ zapo) preservando o
 * histórico. Desvincula o device atual e abre pareamento fresh no motor novo.
 */
/** Pré-registra a intenção de migração QR→Cloud (cobre conector antigo). */
export function registerMigrationIntent(numberId: number): Promise<{ ok: boolean }> {
  return apiRequest(`/whatsapp-numbers/${numberId}/migration-intent`, { method: 'POST', body: {} });
}

/** Migração Cloud/Coexistência → QR (preserva histórico; abre pareamento). */
export function migrateChannelToQr(
  numberId: number,
  provider: 'whatsmeow' | 'zapo' = 'zapo',
): Promise<{ whatsapp_number_id: number; provider: string; status: string }> {
  return apiRequest(`/whatsapp-numbers/${numberId}/migrate-to-qr`, {
    method: 'POST',
    body: { provider },
  });
}

export async function convertQrChannel(
  numberId: number,
  targetProvider: 'whatsmeow' | 'zapo',
): Promise<void> {
  await apiRequest<void>(`/whatsapp-numbers/${numberId}/qr-code/convert`, {
    method: 'POST',
    body: { target_provider: targetProvider },
  });
}

/**
 * Força worker fechar socket whatsmeow atual e reabrir — gera QR novo imediato.
 * Usado pelo botão "Gerar novo QR" quando o QR anterior expirou.
 */
export async function refreshQrChannel(numberId: number): Promise<void> {
  await apiRequest<{ ok: true }>(`/whatsapp-numbers/${numberId}/qr-code/refresh`, {
    method: 'POST',
    body: {},
  });
}

/**
 * Força restart do canal preservando auth state (creds + signal keys).
 * Pra canal revogado: reconecta SEM gerar QR (identidade Signal preservada,
 * sessões com contatos antigos mantidas — sem "Aguardando mensagem").
 */
export async function restartQrChannel(numberId: number): Promise<void> {
  await apiRequest<{ ok: true }>(`/whatsapp-numbers/${numberId}/qr-code/restart`, {
    method: 'POST',
    body: {},
  });
}

/**
 * Exclusão permanente do canal: marca deleted_at no DB (UI esconde imediato)
 * e enfileira purge no worker. Worker apaga messages/tickets/whatsmeow_device
 * em batches no background, finalizando com DELETE da row do canal.
 * Contatos preservados.
 */
export async function purgeQrChannel(numberId: number): Promise<void> {
  await apiRequest<{ ok: true }>(
    `/whatsapp-numbers/${numberId}/qr-code/permanent?confirmation=EXCLUIR`,
    { method: 'DELETE' },
  );
}

/**
 * Exclusão permanente de canal Cloud API (meta_official): espelha purgeQrChannel.
 * Best-effort Meta (deregister + revoke + unsubscribe) + soft hide + enfileira
 * purge no worker-meta-ingest. Worker apaga messages/tickets/events/media em
 * batches. Contatos preservados.
 */
export async function purgeMetaChannel(numberId: number): Promise<void> {
  await apiRequest<{ ok: true }>(`/whatsapp-numbers/${numberId}/permanent?confirmation=EXCLUIR`, {
    method: 'DELETE',
  });
}

export interface UpdateCredentialsInput {
  phoneNumberId?: string | undefined;
  wabaId?: string | undefined;
  accessToken?: string | undefined;
}

export function updateChannelCredentials(id: number, input: UpdateCredentialsInput): Promise<void> {
  const body: Record<string, string> = {};
  if (input.phoneNumberId) body.phone_number_id = input.phoneNumberId;
  if (input.wabaId) body.waba_id = input.wabaId;
  if (input.accessToken) body.access_token = input.accessToken;
  return apiRequest<void>(`/whatsapp-numbers/${id}/credentials`, { method: 'PATCH', body });
}

export interface WebhookInfo {
  webhookUrl: string | null;
  verifyToken: string | null;
}

export async function getWebhookInfo(): Promise<WebhookInfo> {
  const d = await apiRequest<{ webhook_url: string | null; verify_token: string | null }>(
    '/whatsapp-accounts/webhook-info',
  );
  return { webhookUrl: d.webhook_url, verifyToken: d.verify_token };
}

// ─── Digitador (válvula 24h — saída de texto pelo app) ──────────────────

export interface DigitadorDevice {
  id: number;
  name: string;
  online: boolean;
  last_seen_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

/** Liga/desliga a saída de texto pelo app neste canal. */
export function setDigitadorEnabled(id: number, enabled: boolean): Promise<void> {
  return apiRequest<void>(`/whatsapp-numbers/${id}/digitador`, {
    method: 'PATCH',
    body: { enabled },
  });
}

export async function listDigitadorDevices(id: number): Promise<DigitadorDevice[]> {
  const d = await apiRequest<{ items: DigitadorDevice[] }>(
    `/whatsapp-numbers/${id}/digitador/devices`,
  );
  return d.items;
}

/** Cria um device — o token cru volta só agora (mostrar 1x). */
export function createDigitadorDevice(
  id: number,
  name: string,
): Promise<{ id: number; name: string; token: string; server_url?: string | null }> {
  return apiRequest<{ id: number; name: string; token: string; server_url?: string | null }>(
    `/whatsapp-numbers/${id}/digitador/devices`,
    { method: 'POST', body: { name } },
  );
}

export function revokeDigitadorDevice(deviceId: number): Promise<void> {
  return apiRequest<void>(`/digitador/devices/${deviceId}`, { method: 'DELETE' });
}
